import { useEffect, useState } from "react";

const keypad = [
  { num: "1", letters: "" },
  { num: "2", letters: "ABC" },
  { num: "3", letters: "DEF" },
  { num: "4", letters: "GHI" },
  { num: "5", letters: "JKL" },
  { num: "6", letters: "MNO" },
  { num: "7", letters: "PQRS" },
  { num: "8", letters: "TUV" },
  { num: "9", letters: "WXYZ" },
  { num: "", letters: "" },
  { num: "0", letters: "" },
  { num: "", letters: "" },
];

// Clock Component
const ClockDisplay = () => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (date) => {
    return date.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      day: 'numeric',
      month: 'long'
    });
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center'
    }}>
      <div style={{
        fontSize: '16px',
        fontWeight: '500',
        marginBottom: '8px',
        opacity: 0.9
      }}>
        {formatDate(currentTime)}
      </div>
      
      <div style={{
        fontSize: '72px',
        fontWeight: '200',
        lineHeight: '1',
        textShadow: '0 2px 10px rgba(0,0,0,0.3)'
      }}>
        {formatTime(currentTime)}
      </div>
    </div>
  );
};

// Bottom Controls Component
const BottomControls = () => {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '30px 40px 50px',
      marginBottom: '20px'
    }}>
      <div style={{
        width: '50px',
        height: '50px',
        background: 'rgba(255,255,255,0.2)',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(10px)',
        fontSize: '24px'
      }}>
        🔦
      </div>
      
      <div style={{
        width: '50px',
        height: '50px',
        background: 'rgba(255,255,255,0.2)',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(10px)',
        fontSize: '24px'
      }}>
        📷
      </div>
    </div>
  );
};

// Main App Component
const LockScreen = () => {
  const [showPasscode, setShowPasscode] = useState(false);
  const [pin, setPin] = useState("");
  const [bgImage, setBgImage] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [apiId, setApiId] = useState("");
  const [emergencyText, setEmergencyText] = useState("Emergency");
  const [touchStartY, setTouchStartY] = useState(0);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [apiEnabled, setApiEnabled] = useState(false);

  // Initialize from localStorage
  useEffect(() => {
    const savedImage = localStorage.getItem("lockscreenImage") || "";
    const savedApiId = localStorage.getItem("lockscreenApiId") || "";
    const savedApiEnabled = localStorage.getItem("lockscreenApiEnabled") === "true";
    setBgImage(savedImage);
    setApiId(savedApiId);
    setApiEnabled(savedApiEnabled);
  }, []);

  // Handle swipe gestures
  useEffect(() => {
    const handleTouchStart = (e) => {
      if (e.touches.length === 1) {
        setTouchStartY(e.touches[0].clientY);
      }
      // 3-finger swipe for settings
      if (e.touches.length === 3) {
        const deltaY = e.touches[0].clientY - touchStartY;
        if (deltaY > 100) {
          setSettingsOpen(true);
        }
      }
    };

    const handleTouchMove = (e) => {
      if (!showPasscode && e.touches.length === 1) {
        const currentY = e.touches[0].clientY;
        const deltaY = touchStartY - currentY;
        
        if (deltaY > 0) { // Swiping up
          setSwipeOffset(Math.min(deltaY, window.innerHeight));
        }
      }
    };

    const handleTouchEnd = (e) => {
      if (!showPasscode && swipeOffset > 100) {
        setShowPasscode(true);
        setSwipeOffset(0);
      } else {
        setSwipeOffset(0);
      }
    };

    window.addEventListener("touchstart", handleTouchStart);
    window.addEventListener("touchmove", handleTouchMove);
    window.addEventListener("touchend", handleTouchEnd);
    
    return () => {
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [touchStartY, swipeOffset, showPasscode]);

  // Handle number input for passcode
  const handleNumberClick = (num) => {
    if (pin.length < 4) setPin(pin + num);
  };
  
  const handleDelete = () => setPin(pin.slice(0, -1));

  // Fetch API data
  const fetchApiData = async () => {
    try {
      if (!apiId || !apiEnabled) return;
      const res = await fetch(`https://11z.co/_w/${apiId}/selection`);
      const json = await res.json();
      if (json.value) {
        setEmergencyText(json.value);
      }
    } catch (err) {
      console.error("API Error:", err);
    }
  };

  useEffect(() => {
    let firstTimeout;
    let interval;

    firstTimeout = setTimeout(() => {
      fetchApiData();
      interval = setInterval(() => {
        setEmergencyText("Emergency");
        fetchApiData();
      }, 3000);
    }, 30000);

    return () => {
      clearTimeout(firstTimeout);
      clearInterval(interval);
    };
  }, [apiId]);

  // 3-finger swipe detection
  useEffect(() => {
    let touchStartY = 0;
    const handleTouchStart = (e) => {
      if (e.touches.length === 3) touchStartY = e.touches[0].clientY;
    };
    const handleTouchMove = (e) => {
      if (e.touches.length === 3) {
        const deltaY = e.touches[0].clientY - touchStartY;
        if (deltaY > 100) {
          setSettingsOpen(true);
        }
      }
    };
    window.addEventListener("touchstart", handleTouchStart);
    window.addEventListener("touchmove", handleTouchMove);
    return () => {
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
    };
  }, []);

  // Handle image selection
  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageDataUrl = e.target.result;
        setBgImage(imageDataUrl);
        localStorage.setItem("lockscreenImage", imageDataUrl);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle save settings
  const handleSaveSettings = () => {
    localStorage.setItem("lockscreenApiEnabled", apiEnabled.toString());
    if (apiEnabled && apiId) {
      localStorage.setItem("lockscreenApiId", apiId);
    } else if (!apiEnabled) {
      localStorage.removeItem("lockscreenApiId");
      setApiId("");
    }
    setSettingsOpen(false);
  };

  // Clear storage
  const handleClear = () => {
    localStorage.removeItem("lockscreenImage");
    localStorage.removeItem("lockscreenApiId");
    localStorage.removeItem("lockscreenApiEnabled");
    setApiId("");
    setBgImage("");
    setPin("");
    setEmergencyText("Emergency");
    setApiEnabled(false);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      overflow: 'hidden',
      fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif'
    }}>
      
      {/* Lock Screen Background - NO blur (clear image) */}
      {!showPasscode && (
        <>
          {/* Background Image WITHOUT Blur */}
          {bgImage && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundImage: `url(${bgImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              zIndex: -2
            }} />
          )}
          
          {/* Light Dark Overlay */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: bgImage ? 'rgba(0,0,0,0.2)' : 'black',
            zIndex: -1
          }} />
        </>
      )}

      {/* Passcode Screen Background - WITH blur */}
      {showPasscode && (
        <>
          {/* Background Image WITH Blur */}
          {bgImage && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundImage: `url(${bgImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              filter: 'blur(15px)',
              transform: 'scale(1.1)',
              zIndex: -2
            }} />
          )}
          
          {/* Dark Overlay */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: bgImage ? 'rgba(0,0,0,0.4)' : 'black',
            zIndex: -1
          }} />
        </>
      )}

      {/* Lock Screen */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        color: 'white',
        transform: showPasscode ? `translateY(-${window.innerHeight}px)` : `translateY(-${swipeOffset}px)`,
        transition: showPasscode ? 'transform 0.3s ease-out' : 'none',
        zIndex: 1
      }}>
        {/* Clock Section */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          marginTop: '-50px'
        }}>
          <ClockDisplay />
        </div>

        {/* Bottom Section */}
        <BottomControls />

        {/* Swipe Up Indicator */}
        <div style={{
          position: 'absolute',
          bottom: '10px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '134px',
          height: '5px',
          background: 'rgba(255,255,255,0.6)',
          borderRadius: '3px'
        }} />
      </div>

      {/* Passcode Screen */}
      <div style={{
        position: 'absolute',
        top: showPasscode ? 0 : '100%',
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        transition: showPasscode ? 'top 0.3s ease-out' : 'none',
        zIndex: 2
      }}>
        {/* Lock Icon */}
        <div style={{ fontSize: '2rem', marginBottom: '1.5rem' }}>🔒</div>

        {/* Title */}
        <h2 style={{ 
          marginBottom: '1rem', 
          fontSize: '1.2rem', 
          fontWeight: 'normal',
          margin: '0 0 1rem 0'
        }}>
          Enter Passcode
        </h2>

        {/* Dots */}
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem' }}>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} style={{
              width: '15px',
              height: '15px',
              border: '2px solid gray',
              borderRadius: '50%',
              background: pin.length > i ? 'white' : 'transparent'
            }} />
          ))}
        </div>

        {/* Keypad */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 80px)',
          gap: '1rem',
          justifyContent: 'center'
        }}>
          {keypad.map((item, index) =>
            item.num ? (
              <button key={index} onClick={() => handleNumberClick(item.num)} style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: bgImage ? 'rgba(255, 255, 255, 0.15)' : 'rgba(28, 28, 28, 0.8)',
                backdropFilter: 'blur(20px)',
                border: bgImage ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                transition: 'all 0.2s ease',
                boxShadow: bgImage ? '0 4px 15px rgba(0, 0, 0, 0.1)' : 'none'
              }}
              onMouseEnter={(e) => {
                if (bgImage) {
                  e.target.style.background = 'rgba(255, 255, 255, 0.25)';
                  e.target.style.transform = 'scale(1.05)';
                } else {
                  e.target.style.background = 'rgba(51, 51, 51, 0.8)';
                }
              }}
              onMouseLeave={(e) => {
                if (bgImage) {
                  e.target.style.background = 'rgba(255, 255, 255, 0.15)';
                  e.target.style.transform = 'scale(1)';
                } else {
                  e.target.style.background = 'rgba(28, 28, 28, 0.8)';
                }
              }}
              >
                <span style={{ 
                  fontSize: '1.5rem', 
                  fontWeight: 'bold',
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)'
                }}>
                  {item.num}
                </span>
                <span style={{ 
                  fontSize: '0.7rem', 
                  color: bgImage ? 'rgba(255, 255, 255, 0.8)' : 'gray', 
                  marginTop: '2px',
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)'
                }}>
                  {item.letters}
                </span>
              </button>
            ) : (
              <div key={index} />
            )
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '260px',
          marginTop: '2rem'
        }}>
          <button style={{
            background: 'none',
            border: 'none',
            color: 'white',
            cursor: 'pointer',
            fontSize: '1rem'
          }}>
            {emergencyText}
          </button>
          {pin.length === 0 ? (
            <button onClick={() => setShowPasscode(false)} style={{
              background: 'none',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              fontSize: '1rem'
            }}>
              Cancel
            </button>
          ) : (
            <button onClick={handleDelete} style={{
              background: 'none',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              fontSize: '1rem'
            }}>
              Delete
            </button>
          )}
        </div>
      </div>

      {/* Settings Dialog */}
      {settingsOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#222',
            color: 'white',
            padding: '20px',
            borderRadius: '12px',
            width: '320px',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <h3 style={{ marginTop: 0 }}>Settings</h3>
            
            <div style={{ marginBottom: '15px', textAlign: 'left' }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center',
                marginBottom: '10px',
                cursor: 'pointer'
              }}>
                <span style={{ marginRight: '10px' }}>Enable API:</span>
                <div
                  onClick={() => setApiEnabled(!apiEnabled)}
                  style={{
                    width: '50px',
                    height: '26px',
                    backgroundColor: apiEnabled ? '#4CAF50' : '#ccc',
                    borderRadius: '13px',
                    position: 'relative',
                    transition: 'background-color 0.3s',
                    cursor: 'pointer'
                  }}
                >
                  <div
                    style={{
                      width: '22px',
                      height: '22px',
                      backgroundColor: 'white',
                      borderRadius: '50%',
                      position: 'absolute',
                      top: '2px',
                      left: apiEnabled ? '26px' : '2px',
                      transition: 'left 0.3s',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}
                  />
                </div>
              </label>
            </div>

            {apiEnabled && (
              <div style={{ marginBottom: '15px', textAlign: 'left' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}>
                  API ID:
                </label>
                <input
                  type="text"
                  value={apiId}
                  onChange={(e) => setApiId(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #555',
                    background: '#333',
                    color: 'white',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            )}

            <div style={{ marginBottom: '20px', textAlign: 'left' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>
                Background Image:
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #555',
                  background: '#333',
                  color: 'white',
                  boxSizing: 'border-box'
                }}
              />
              {bgImage && (
                <div style={{ 
                  marginTop: '10px', 
                  fontSize: '0.9rem', 
                  color: '#4CAF50' 
                }}>
                  ✓ Image selected and saved
                </div>
              )}
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'space-around',
              gap: '10px',
              marginBottom: '15px'
            }}>
              <button onClick={handleSaveSettings} style={{
                background: '#4CAF50',
                border: 'none',
                padding: '10px 16px',
                borderRadius: '6px',
                cursor: 'pointer',
                color: 'white',
                flex: 1
              }}>
                Save
              </button>
              <button onClick={handleClear} style={{
                background: '#f44336',
                border: 'none',
                padding: '10px 16px',
                borderRadius: '6px',
                cursor: 'pointer',
                color: 'white',
                flex: 1
              }}>
                Clear
              </button>
              <button onClick={() => setSettingsOpen(false)} style={{
                background: '#666',
                border: 'none',
                padding: '10px 16px',
                borderRadius: '6px',
                cursor: 'pointer',
                color: 'white',
                flex: 1
              }}>
                Close
              </button>
            </div>

            {/* Footer with Made with love */}
            <div style={{
              borderTop: '1px solid #444',
              paddingTop: '12px',
              fontSize: '0.85rem',
              color: '#aaa',
              textAlign: 'center'
            }}>
              Made with ❤️ by Bhavik Shah
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LockScreen;